import React, { useEffect, useState, useMemo } from 'react';
import { Activity, AlertTriangle, CheckCircle, Info, XCircle, Zap, Eye, EyeOff, Bot, Filter, Search, Clock } from 'lucide-react';
import { getAuthInternalHeaders } from '../utils/internalApi';

const API_BASE = window.location.origin;

// Storage key for filter persistence
const FILTER_STORAGE_KEY = 'guardian_console_filters';

// Internal Badge Component - displays INTERNAL (reason) pill for test/bot traffic
const InternalBadge = ({ reason }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30">
    <Bot className="w-3 h-3" />
    INTERNAL{reason ? ` (${reason})` : ''}
  </span>
);

// Time window options
const TIME_WINDOWS = [
  { value: '1h', label: 'Last 1h', ms: 60 * 60 * 1000 },
  { value: '6h', label: 'Last 6h', ms: 6 * 60 * 60 * 1000 },
  { value: '24h', label: 'Last 24h', ms: 24 * 60 * 60 * 1000 },
  { value: '7d', label: 'Last 7d', ms: 7 * 24 * 60 * 60 * 1000 },
  { value: 'all', label: 'All', ms: Infinity },
];

// Severity options
const SEVERITY_OPTIONS = ['all', 'critical', 'error', 'warning', 'info'];

// Load saved filters from localStorage
const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load saved filters:', e);
  }
  return {
    severity: 'all',
    agent: 'all',
    searchText: '',
    timeWindow: '24h',
  };
};

// Save filters to localStorage
const saveFilters = (filters) => {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    console.warn('Failed to save filters:', e);
  }
};

export const GuardianConsole = () => {
  const [metrics, setMetrics] = useState(null);
  const [actions, setActions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Internal traffic toggle (default: hide internal)
  const [showInternal, setShowInternal] = useState(false);
  
  // Filter state - load from localStorage
  const [filters, setFilters] = useState(loadSavedFilters);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Save filters whenever they change
  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  // Current timestamp state for time-based filtering (updates every 10s with data fetch)
  const [currentTime, setCurrentTime] = useState(Date.now());

  const fetchData = async () => {
    try {
      // Update current time for filters
      setCurrentTime(Date.now());
      
      // Use internal headers - Guardian Console is an operator surface
      const headers = getAuthInternalHeaders('guardian');
      const [metricsRes, actionsRes, alertsRes, agentsRes, statusRes] = await Promise.all([
        fetch(`${API_BASE}/api/guardians/metrics`, { headers }),
        fetch(`${API_BASE}/api/guardians/actions?limit=50`, { headers }),
        fetch(`${API_BASE}/api/guardians/alerts?limit=50`, { headers }),
        fetch(`${API_BASE}/api/guardians/agents`, { headers }),
        fetch(`${API_BASE}/api/guardians/status`, { headers }),
      ]);

      setMetrics(await metricsRes.json());
      setActions((await actionsRes.json()).actions);
      setAlerts((await alertsRes.json()).alerts);
      setAgents((await agentsRes.json()).agents);
      setStatus(await statusRes.json());
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch Guardian data:', error);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  // Get unique agent names for filter dropdown
  const uniqueAgentNames = useMemo(() => {
    const names = new Set(alerts.map(a => a.agent_name).filter(Boolean));
    return ['all', ...Array.from(names).sort()];
  }, [alerts]);

  // Apply filters to alerts (client-side)
  const filteredAlerts = useMemo(() => {
    const timeWindow = TIME_WINDOWS.find(t => t.value === filters.timeWindow);
    const cutoff = timeWindow ? currentTime - timeWindow.ms : 0;

    return alerts.filter(alert => {
      // Internal filter (existing behavior)
      if (!showInternal && alert.internal) {
        return false;
      }

      // Severity filter
      if (filters.severity !== 'all' && alert.severity !== filters.severity) {
        return false;
      }

      // Agent filter
      if (filters.agent !== 'all' && alert.agent_name !== filters.agent) {
        return false;
      }

      // Time window filter
      if (timeWindow && timeWindow.ms !== Infinity) {
        const alertTime = new Date(alert.timestamp).getTime();
        if (alertTime < cutoff) {
          return false;
        }
      }

      // Text search filter
      if (filters.searchText) {
        const search = filters.searchText.toLowerCase();
        const matchesType = alert.type?.toLowerCase().includes(search);
        const matchesMessage = alert.message?.toLowerCase().includes(search);
        const matchesAgent = alert.agent_name?.toLowerCase().includes(search);
        if (!matchesType && !matchesMessage && !matchesAgent) {
          return false;
        }
      }

      return true;
    });
  }, [alerts, filters, showInternal, currentTime]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-400';
      case 'warning': return 'text-yellow-400';
      case 'degraded': return 'text-orange-400';
      case 'critical': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'warning': return <AlertTriangle className="w-6 h-6 text-yellow-400" />;
      case 'degraded': return <AlertTriangle className="w-6 h-6 text-orange-400" />;
      case 'critical': return <XCircle className="w-6 h-6 text-red-400" />;
      default: return <Activity className="w-6 h-6 text-gray-400" />;
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'info': return <Info className="w-4 h-4 text-blue-400" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'error': return <XCircle className="w-4 h-4 text-orange-400" />;
      case 'critical': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <Info className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white p-4 sm:p-6">
        {/* Header skeleton */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-700 rounded-lg animate-pulse" />
            <div>
              <div className="h-8 w-48 bg-gray-700 rounded animate-pulse mb-2" />
              <div className="h-4 w-32 bg-gray-800 rounded animate-pulse" />
            </div>
          </div>
        </div>
        
        {/* Metrics skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-gray-700">
              <div className="h-4 w-20 bg-gray-700 rounded animate-pulse mb-4" />
              <div className="space-y-2">
                <div className="h-3 w-full bg-gray-700 rounded animate-pulse" />
                <div className="h-3 w-3/4 bg-gray-700 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-gray-700 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        
        {/* Alerts skeleton */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <div className="h-6 w-32 bg-gray-700 rounded animate-pulse" />
          </div>
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-700/50 rounded-lg p-4 border-l-4 border-gray-600">
                <div className="flex items-start gap-3">
                  <div className="w-4 h-4 bg-gray-600 rounded animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 w-24 bg-gray-600 rounded animate-pulse mb-2" />
                    <div className="h-3 w-full bg-gray-600 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <p className="text-center text-gray-500 mt-8">Loading Guardian Console...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" />
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
                AI Guardians
              </h1>
              <p className="text-sm sm:text-base text-gray-400 mt-1">Real-time system monitoring</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            {/* Internal Traffic Toggle */}
            <button
              onClick={() => setShowInternal(!showInternal)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm ${
                showInternal
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-300'
              }`}
              title={showInternal ? 'Showing internal traffic' : 'Hiding internal traffic'}
              aria-label={showInternal ? 'Hide internal traffic' : 'Show internal traffic'}
            >
              {showInternal ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="hidden sm:inline">Internal</span>
            </button>
            
            {status && (
              <div className="flex items-center gap-2 sm:gap-3 bg-gray-800 px-4 py-2 sm:px-6 sm:py-3 rounded-lg">
                {getStatusIcon(status.status)}
                <div>
                  <div className="text-xs sm:text-sm text-gray-400">Status</div>
                  <div className={`text-sm sm:text-lg font-semibold ${getStatusColor(status.status)}`}>
                    {status.status.toUpperCase()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Internal Traffic Info Banner */}
        {showInternal && (
          <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2 flex items-center gap-3">
            <Bot className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-200">
              <strong>Debug Mode:</strong> Showing internal traffic from Playwright tests, agents, and admin tools.
            </p>
          </div>
        )}
      </div>

      {/* Metrics Grid */}
      {metrics && metrics.available && (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {/* HTTP Metrics */}
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-purple-500/20">
            <h3 className="text-xs sm:text-sm font-semibold text-purple-400 mb-3 sm:mb-4">HTTP Traffic</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">RPS</span>
                <span className="font-semibold">{metrics.http.rps}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Latency</span>
                <span className="font-semibold">{metrics.http.avg_response_time_ms}ms</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Error Rate</span>
                <span className={`font-semibold ${metrics.http.error_rate > 5 ? 'text-red-400' : 'text-green-400'}`}>
                  {metrics.http.error_rate}%
                </span>
              </div>
            </div>
          </div>

          {/* Database Metrics */}
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-blue-500/20">
            <h3 className="text-xs sm:text-sm font-semibold text-blue-400 mb-3 sm:mb-4">Database</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Latency</span>
                <span className="font-semibold">{metrics.database.latency_ms}ms</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Error Rate</span>
                <span className={`font-semibold ${metrics.database.error_rate > 2 ? 'text-red-400' : 'text-green-400'}`}>
                  {metrics.database.error_rate}%
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Slow Queries</span>
                <span className="font-semibold">{metrics.database.slow_queries}</span>
              </div>
            </div>
          </div>

          {/* System Metrics */}
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-green-500/20">
            <h3 className="text-xs sm:text-sm font-semibold text-green-400 mb-3 sm:mb-4">System</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">CPU</span>
                <span className={`font-semibold ${metrics.system.cpu_percent > 80 ? 'text-red-400' : 'text-green-400'}`}>
                  {metrics.system.cpu_percent}%
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Memory</span>
                <span className={`font-semibold ${metrics.system.memory_percent > 80 ? 'text-red-400' : 'text-green-400'}`}>
                  {metrics.system.memory_percent}%
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Disk</span>
                <span className="font-semibold">{metrics.system.disk_percent}%</span>
              </div>
            </div>
          </div>

          {/* Agent Status */}
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-pink-500/20">
            <h3 className="text-xs sm:text-sm font-semibold text-pink-400 mb-3 sm:mb-4">Agents</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total</span>
                <span className="font-semibold">{agents.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Active</span>
                <span className="font-semibold text-green-400">
                  {agents.filter(a => a.enabled).length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Actions</span>
                <span className="font-semibold">{actions.length}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Alerts & Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Alerts with Filters */}
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-lg sm:text-xl font-semibold">Recent Alerts</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  {filteredAlerts.length} / {alerts.length}
                </span>
                <button
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                    filtersExpanded ? 'bg-purple-500/20 text-purple-300' : 'bg-gray-700 text-gray-400 hover:text-gray-300'
                  }`}
                  aria-label="Toggle filters"
                  aria-expanded={filtersExpanded}
                  data-testid="guardian-filter-toggle"
                >
                  <Filter className="w-3 h-3" />
                  Filters
                </button>
              </div>
            </div>
            
            {/* Filter Controls */}
            {filtersExpanded && (
              <div className="mt-4 space-y-3" data-testid="guardian-filter-controls">
                {/* Row 1: Severity + Agent */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-gray-400 mb-1 block">Severity</label>
                    <select
                      value={filters.severity}
                      onChange={(e) => updateFilter('severity', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-purple-500 focus:outline-none"
                      data-testid="guardian-severity-filter"
                    >
                      {SEVERITY_OPTIONS.map(sev => (
                        <option key={sev} value={sev}>
                          {sev === 'all' ? 'All Severities' : sev.charAt(0).toUpperCase() + sev.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-400 mb-1 block">Agent</label>
                    <select
                      value={filters.agent}
                      onChange={(e) => updateFilter('agent', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-purple-500 focus:outline-none"
                      data-testid="guardian-agent-filter"
                    >
                      {uniqueAgentNames.map(name => (
                        <option key={name} value={name}>
                          {name === 'all' ? 'All Agents' : name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Row 2: Time Window + Search */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="sm:w-1/3">
                    <label className="text-xs text-gray-400 mb-1 block">Time Window</label>
                    <div className="flex items-center gap-1 bg-gray-700 border border-gray-600 rounded px-2 py-1.5">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <select
                        value={filters.timeWindow}
                        onChange={(e) => updateFilter('timeWindow', e.target.value)}
                        className="flex-1 bg-transparent text-sm text-white focus:outline-none"
                        data-testid="guardian-time-filter"
                      >
                        {TIME_WINDOWS.map(tw => (
                          <option key={tw.value} value={tw.value}>{tw.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-400 mb-1 block">Search</label>
                    <div className="flex items-center gap-1 bg-gray-700 border border-gray-600 rounded px-2 py-1.5">
                      <Search className="w-3 h-3 text-gray-400" />
                      <input
                        type="text"
                        value={filters.searchText}
                        onChange={(e) => updateFilter('searchText', e.target.value)}
                        placeholder="Search type, message, agent..."
                        className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                        data-testid="guardian-search-filter"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4 max-h-96 overflow-y-auto">
            {filteredAlerts.length === 0 ? (
              <div className="text-center py-12">
                {alerts.length === 0 ? (
                  // True empty state - no alerts at all
                  <div className="space-y-4">
                    <div className="w-16 h-16 mx-auto bg-gray-700/50 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-200 mb-1">All Clear</h3>
                      <p className="text-sm text-gray-400 max-w-sm mx-auto">
                        No alerts detected. The AI Guardians are monitoring your system and will flag any issues here.
                      </p>
                    </div>
                    <div className="flex items-center justify-center gap-3 pt-2">
                      <button
                        onClick={fetchData}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
                      >
                        Refresh
                      </button>
                      <a
                        href="https://docs.unitylove.app/guardians"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                      >
                        View Docs
                      </a>
                    </div>
                  </div>
                ) : (
                  // Filters hiding results
                  <div className="space-y-4">
                    <div className="w-16 h-16 mx-auto bg-gray-700/50 rounded-full flex items-center justify-center">
                      <Filter className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-200 mb-1">No Matching Alerts</h3>
                      <p className="text-sm text-gray-400 max-w-sm mx-auto">
                        {alerts.length} alert{alerts.length !== 1 ? 's' : ''} exist but none match your current filters.
                      </p>
                    </div>
                    <button
                      onClick={() => setFilters({
                        severity: 'all',
                        agent: 'all',
                        searchText: '',
                        timeWindow: 'all',
                      })}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAlerts.map((alert, idx) => (
                  <div 
                    key={idx} 
                    className={`bg-gray-700/50 rounded-lg p-3 sm:p-4 border-l-4 ${
                      alert.internal ? 'border-amber-400/50' : 'border-blue-400'
                    }`}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      {getSeverityIcon(alert.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-1 sm:gap-2">
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            <span className="font-semibold text-xs sm:text-sm text-blue-400">{alert.agent_name}</span>
                            {/* INTERNAL Badge - only show when toggle is ON and alert is internal */}
                            {showInternal && alert.internal && (
                              <InternalBadge reason={alert.internal_reason} />
                            )}
                          </div>
                          <span className="text-xs text-gray-500">{formatTimestamp(alert.timestamp)}</span>
                        </div>
                        <p className="text-xs sm:text-sm break-words">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg sm:text-xl font-semibold">Recent Actions</h2>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {actions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recent actions</p>
            ) : (
              <div className="space-y-3">
                {actions.map((action, idx) => (
                  <div key={idx} className="bg-gray-700/50 rounded-lg p-3 sm:p-4 border-l-4 border-purple-400">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <Zap className="w-4 h-4 text-purple-400 mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            <span className="font-semibold text-xs sm:text-sm text-purple-400">{action.agent_name}</span>
                            <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">
                              {action.action_type}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">{formatTimestamp(action.timestamp)}</span>
                        </div>
                        <p className="text-xs sm:text-sm break-words">{action.reason}</p>
                        <div className="mt-2 text-xs text-gray-400">
                          Confidence: {(action.confidence * 100).toFixed(0)}%
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardianConsole;
